import type { LegalDocumentSource } from '@repo/api-contracts';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { ComingSoonPage } from '@/pages/coming-soon';
import { PRIVACY_POLICY_PATH, TERMS_OF_SERVICE_PATH } from '@/shared/config';

/** 약관 페이지를 실은 빌드에서만 불러오는 것 — 약관 페이지와 두 문서. */
export interface LegalDraftPageModules {
  readonly LegalDocumentPage: ComponentType<{
    readonly title: string;
    readonly document: LegalDocumentSource;
  }>;
  readonly drafts: {
    readonly TERMS_OF_SERVICE_DRAFT: LegalDocumentSource;
    readonly PRIVACY_POLICY_DRAFT: LegalDocumentSource;
  };
}

export type LegalDraftPageLoader = () => Promise<LegalDraftPageModules>;

/** 주소·제목(푸터 링크 이름과 같다)·그 주소의 문서. */
const LEGAL_PAGES: readonly {
  readonly path: string;
  readonly title: string;
  readonly pick: (drafts: LegalDraftPageModules['drafts']) => LegalDocumentSource;
}[] = [
  {
    path: PRIVACY_POLICY_PATH,
    title: '개인정보처리방침',
    pick: (drafts) => drafts.PRIVACY_POLICY_DRAFT,
  },
  {
    path: TERMS_OF_SERVICE_PATH,
    title: '이용약관',
    pick: (drafts) => drafts.TERMS_OF_SERVICE_DRAFT,
  },
];

/**
 * 이용약관·개인정보처리방침 공개 라우트. 불러오기(`load`)가 없으면 — 약관 페이지 스위치를 끈 빌드 — 지금의 준비 중 페이지다.
 * 있으면 처음 열 때 페이지와 문서를 불러와(react-router 지연 로딩) 본문을 그린다. 스위치는 빌드 시점 상수라
 * `legal-draft-loader.ts` 가 정한다.
 */
export const legalRoutes = (load: LegalDraftPageLoader | null): RouteObject[] =>
  LEGAL_PAGES.map(({ path, title, pick }) =>
    load === null
      ? { path, element: <ComingSoonPage title={title} /> }
      : {
          path,
          lazy: async () => {
            const { LegalDocumentPage, drafts } = await load();
            return { element: <LegalDocumentPage title={title} document={pick(drafts)} /> };
          },
        },
  );
